import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withModifiers as _withModifiers, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 2 }
const _hoisted_4 = { key: 3 }
const _hoisted_5 = { key: 4 }
const _hoisted_6 = {
  key: 5,
  class: "next_button"
}

import { computed, toRef, type PropType } from "vue";

// component props

export default /*@__PURE__*/_defineComponent({
  __name: 'PaginationComponent',
  props: {
  // Configuration props
  totalItems: {
    type: Number,
    required: true,
  },
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  modelValue: {
    type: Number,
    required: true,
  },
  maxPagesShown: {
    type: Number,
    default: 5,
  },
  dir: {
    type: String as PropType<"ltr" | "rtl">,
    default: "ltr",
  },
  type: {
    type: String as PropType<"link" | "button">,
    default: "button",
  },
  onClick: {
    type: Function,
  },
  locale: {
    type: String as PropType<"en" | "ar" | "ir">,
    default: "en",
  },
  prevButtonContent: {
    type: String,
    default: "<",
  },
  nextButtonContent: {
    type: String,
    default: ">",
  },
  hidePrevNext: {
    type: Boolean,
    default: false,
  },
  hidePrevNextWhenEnds: {
    type: Boolean,
    default: false,
  },
  showBreakpointButtons: {
    type: Boolean,
    default: true,
  },
  disableBreakpointButtons: {
    type: Boolean,
    default: false,
  },
  startingBreakpointContent: {
    type: String,
    default: "...",
  },
  endingBreakpointButtonContent: {
    type: String,
    default: "...",
  },
  showJumpButtons: {
    type: Boolean,
    default: false,
  },
  disablePagination: {
    type: Boolean,
    default: false,
  },
  showEndingButtons: {
    type: Boolean,
    default: false,
  },
  firstPageContent: {
    type: String,
    default: "First",
  },
  lastPageContent: {
    type: String,
    default: "Last",
  },

  // Class props
  backButtonClass: {
    type: String,
    default: "back-button",
  },
  nextButtonClass: {
    type: String,
    default: "next-button",
  },
  firstButtonClass: {
    type: String,
    default: "first-button",
  },
  lastButtonClass: {
    type: String,
    default: "last-button",
  },
  numberButtonsClass: {
    type: String,
    default: "number-buttons",
  },
  startingBreakpointButtonClass: {
    type: String,
    default: "starting-breakpoint-button",
  },
  endingBreakPointButtonClass: {
    type: String,
    default: "ending-breakpoint-button",
  },
  firstPageButtonClass: {
    type: String,
    default: "first-page-button",
  },
  lastPageButtonClass: {
    type: String,
    default: "last-page-button",
  },

  // use this selector above all the other selectors because of css specificity
  paginateButtonsClass: {
    type: String,
    default: "paginate-buttons",
  },
  disabledPaginateButtonsClass: {
    type: String,
    default: "disabled-paginate-buttons",
  },
  activePageClass: {
    type: String,
    default: "active-page",
  },
  paginationContainerClass: {
    type: String,
    default: "pagination-container",
  },
  disabledBreakPointButtonClass: {
    type: String,
    default: "disabled-breakpoint-button",
  },
  backwardJumpButtonClass: {
    type: String,
    default: "backward-jump-button",
  },
  forwardJumpButtonClass: {
    type: String,
    default: "forward-jump-button",
  },
  disabledBackwardJumpButtonClass: {
    type: String,
    default: "disabled-backward-jump-button",
  },
  disabledBackButtonClass: {
    type: String,
    default: "disabled-back-button",
  },
  disabledFirstButtonClass: {
    type: String,
    default: "disabled-first-button",
  },
  disabledLastButtonClass: {
    type: String,
    default: "disabled-last-button",
  },
  disabledNextButtonClass: {
    type: String,
    default: "disabled-next-button",
  },
},
  emits: ["update:modelValue"],
  setup(__props, { emit: __emit }) {

const props = __props;

const currentPageRef = toRef(props, "modelValue");

const emit = __emit;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const onClickHandler = (number: number) => {
  if (number === currentPageRef.value) return;

  if (number > totalPages.value) return;

  if (number < 1) return;
  if (props.disablePagination) return;

  emit("update:modelValue", number);
  props.onClick(number);
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const NumbersLocale = (number: number) => {
  switch (props.locale) {
    case "en":
      return number;
    case "ar":
      return number.toLocaleString("ar-SA");
    case "ir":
      return number.toLocaleString("fa-IR");
    default:
      return number;
  }
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const navigationHandler = (page: number) => {
  if (props.type !== "link") return "";
  return props.linkUrl?.replace("[page]", page.toString());
};

const totalPages = computed(() =>
  Math.ceil(props.totalItems / props.itemsPerPage)
);
// Pagination logic
const paginate = computed(() => {
  let startPage: number, endPage: number;

  if (totalPages.value <= props.maxPagesShown) {
    startPage = 1;
    endPage = totalPages.value;
  } else {
    let maxPagesShownBeforeCurrentPage = Math.floor(props.maxPagesShown / 2);
    let maxPagesShownAfterCurrentPage = Math.ceil(props.maxPagesShown / 2) - 1;
    if (currentPageRef.value <= maxPagesShownBeforeCurrentPage) {
      // current page is at the start of the pagination
      startPage = 1;
      endPage = props.maxPagesShown;
    } else if (
      currentPageRef.value + maxPagesShownAfterCurrentPage >=
      totalPages.value
    ) {
      // current page is at the end of the pagination
      startPage = totalPages.value - props.maxPagesShown + 1;
      endPage = totalPages.value;
    } else {
      // current page is somewhere in the middle of the pagination
      startPage = currentPageRef.value - maxPagesShownBeforeCurrentPage;
      endPage = currentPageRef.value + maxPagesShownAfterCurrentPage;
    }
  }
  // create an array of pages to be displayed
  let pages: any[] = [];
  try {
    pages = Array.from(Array(endPage + 1 - startPage).keys()).map(
      (i) => startPage + i
    );
  } catch (e) {
    //.
  }

  if (props.dir === "rtl") {
    pages = pages.reverse();
  }

  return {
    totalItems: props.totalItems,
    currentPage: currentPageRef.value,
    itemsPerPage: props.itemsPerPage,
    totalPages: totalPages,
    startPage: startPage,
    endPage: endPage,
    pages: pages,
  };
});
// rtl check
const isRtl = computed(() => props.dir === "rtl");
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const backButtonIfCondition = computed(() => {
  if (isRtl.value)
    return (
      !props.hidePrevNextWhenEnds || currentPageRef.value !== totalPages.value
    );

  return !props.hidePrevNextWhenEnds || currentPageRef.value !== 1;
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const nextButtonIfCondition = computed(() => {
  if (isRtl.value)
    return !props.hidePrevNextWhenEnds || currentPageRef.value !== 1;

  return (
    !props.hidePrevNextWhenEnds || currentPageRef.value !== totalPages.value
  );
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const endingBreakPointButtonIfCondition = computed(() => {
  if (isRtl.value) {
    return paginate.value.pages[paginate.value.pages.length - 1] >= 3;
  }

  return (
    paginate.value.pages[paginate.value.pages.length - 1] < totalPages.value - 1
  );
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const firstButtonIfCondition = computed(() => {
  if (isRtl.value) {
    return paginate.value.pages[0] < totalPages.value;
  }

  return paginate.value.pages[0] >= 2;
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const lastButtonIfCondition = computed(() => {
  if (isRtl.value) {
    return paginate.value.pages[paginate.value.pages.length - 1] >= 2;
  }

  return (
    paginate.value.pages[paginate.value.pages.length - 1] < totalPages.value
  );
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const firstPageButtonIfCondition = computed(() => {
  if (currentPageRef.value === 1) return false;
  return true;
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("ul", {
    id: "componentContainer",
    class: _normalizeClass(__props.paginationContainerClass)
  }, [
    (__props.showEndingButtons && firstPageButtonIfCondition.value)
      ? (_openBlock(), _createElementBlock("li", _hoisted_1, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(__props.type === 'button' ? 'button' : 'a'), {
            href: navigationHandler(isRtl.value ? totalPages.value : 1),
            onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (onClickHandler(isRtl.value ? totalPages.value : 1)), ["prevent"])),
            class: _normalizeClass([
          __props.firstPageButtonClass,
          __props.paginateButtonsClass,
          __props.disablePagination ? __props.disabledPaginateButtonsClass : '',
        ]),
            disabled: __props.disablePagination
          }, {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "first-page-button", {}, () => [
                _createTextVNode(_toDisplayString(__props.firstPageContent), 1)
              ])
            ]),
            _: 3
          }, 8, ["href", "class", "disabled"]))
        ]))
      : _createCommentVNode("", true),
    (!__props.hidePrevNext && backButtonIfCondition.value)
      ? (_openBlock(), _createElementBlock("li", _hoisted_2, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(__props.type === 'button' ? 'button' : 'a'), {
            href: 
          navigationHandler(isRtl.value ? currentPageRef.value + 1 : currentPageRef.value - 1)
        ,
            onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (
          onClickHandler(isRtl.value ? currentPageRef.value + 1 : currentPageRef.value - 1)
        ), ["prevent"])),
            class: _normalizeClass([
          __props.backButtonClass,
          __props.paginateButtonsClass,
          __props.disablePagination ? __props.disabledPaginateButtonsClass : '',
          __props.disablePagination ? __props.disabledBackButtonClass : '',
        ]),
            disabled: __props.disablePagination
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, {
                size: "20",
                class: _normalizeClass([currentPageRef.value == 1 ? 'icon_start' : '']),
                icon: "mdi-arrow-left"
              }, null, 8, ["class"])
            ]),
            _: 1
          }, 8, ["href", "class", "disabled"]))
        ]))
      : _createCommentVNode("", true),
    (__props.showBreakpointButtons && firstButtonIfCondition.value)
      ? (_openBlock(), _createElementBlock("li", _hoisted_3, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(__props.type === 'button' ? 'button' : 'a'), {
            href: navigationHandler(isRtl.value ? totalPages.value : 1),
            onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (onClickHandler(isRtl.value ? totalPages.value : 1)), ["prevent"])),
            class: _normalizeClass([
          __props.firstButtonClass,
          __props.paginateButtonsClass,
          __props.disablePagination ? __props.disabledPaginateButtonsClass : '',
          __props.disablePagination ? __props.disabledFirstButtonClass : '',
        ]),
            disabled: __props.disablePagination
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(isRtl.value ? NumbersLocale(totalPages.value) : NumbersLocale(1)), 1)
            ]),
            _: 1
          }, 8, ["href", "class", "disabled"]))
        ]))
      : _createCommentVNode("", true),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginate.value.pages, (page, index) => {
      return (_openBlock(), _createElementBlock("li", { key: index }, [
        (_openBlock(), _createBlock(_resolveDynamicComponent(__props.type === 'button' ? 'button' : 'a'), {
          href: navigationHandler(page),
          onClick: _withModifiers(() => onClickHandler(page), ["prevent"]),
          class: _normalizeClass([
          __props.paginateButtonsClass,
          __props.numberButtonsClass,
          page === currentPageRef.value ? __props.activePageClass : '',
          __props.disablePagination ? __props.disabledPaginateButtonsClass : '',
        ]),
          disabled: __props.disablePagination
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(NumbersLocale(page)), 1)
          ]),
          _: 2
        }, 1032, ["href", "onClick", "class", "disabled"]))
      ]))
    }), 128)),
    (__props.showBreakpointButtons && endingBreakPointButtonIfCondition.value)
      ? (_openBlock(), _createElementBlock("li", _hoisted_4, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(__props.type === 'button' ? 'button' : 'a'), {
            href: 
          navigationHandler(
            __props.disableBreakpointButtons
              ? currentPageRef.value
              : isRtl.value
              ? currentPageRef.value - Math.ceil(__props.maxPagesShown / 2)
              : currentPageRef.value + Math.ceil(__props.maxPagesShown / 2)
          )
        ,
            onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (
          onClickHandler(
            __props.disableBreakpointButtons
              ? currentPageRef.value
              : isRtl.value
              ? currentPageRef.value - Math.ceil(__props.maxPagesShown / 2)
              : currentPageRef.value + Math.ceil(__props.maxPagesShown / 2)
          )
        ), ["prevent"])),
            disabled: __props.disableBreakpointButtons || __props.disablePagination,
            class: _normalizeClass([
          __props.endingBreakPointButtonClass,
          __props.paginateButtonsClass,
          __props.disableBreakpointButtons || __props.disablePagination
            ? `${__props.disabledPaginateButtonsClass} ${__props.disabledBreakPointButtonClass}`
            : '',
        ])
          }, {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "ending-breakpoint-button", {}, () => [
                _createTextVNode(_toDisplayString(__props.endingBreakpointButtonContent), 1)
              ])
            ]),
            _: 3
          }, 8, ["href", "disabled", "class"]))
        ]))
      : _createCommentVNode("", true),
    (__props.showBreakpointButtons && lastButtonIfCondition.value)
      ? (_openBlock(), _createElementBlock("li", _hoisted_5, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(__props.type === 'button' ? 'button' : 'a'), {
            href: navigationHandler(isRtl.value ? 1 : totalPages.value),
            onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (onClickHandler(isRtl.value ? 1 : totalPages.value)), ["prevent"])),
            class: _normalizeClass([
          __props.lastButtonClass,
          __props.paginateButtonsClass,
          __props.disablePagination ? __props.disabledPaginateButtonsClass : '',
          __props.disablePagination ? __props.disabledLastButtonClass : '',
        ]),
            disabled: __props.disablePagination
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(isRtl.value ? NumbersLocale(1) : NumbersLocale(totalPages.value)), 1)
            ]),
            _: 1
          }, 8, ["href", "class", "disabled"]))
        ]))
      : _createCommentVNode("", true),
    (!__props.hidePrevNext && nextButtonIfCondition.value)
      ? (_openBlock(), _createElementBlock("li", _hoisted_6, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(__props.type === 'button' ? 'button' : 'a'), {
            href: 
          navigationHandler(isRtl.value ? currentPageRef.value - 1 : currentPageRef.value + 1)
        ,
            onClick: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (
          onClickHandler(isRtl.value ? currentPageRef.value - 1 : currentPageRef.value + 1)
        ), ["prevent"])),
            class: _normalizeClass([
          __props.paginateButtonsClass,
          __props.nextButtonClass,
          __props.disablePagination ? __props.disabledPaginateButtonsClass : '',
          __props.disablePagination ? __props.disabledNextButtonClass : '',
        ]),
            disabled: __props.disablePagination
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, {
                size: "20",
                class: _normalizeClass([currentPageRef.value === totalPages.value ? 'icon_start' : '']),
                icon: "mdi-arrow-right"
              }, null, 8, ["class"])
            ]),
            _: 1
          }, 8, ["href", "class", "disabled"]))
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})